<template>
  <div class="root-form">
    <Component
      :name="name"
      :is="uiForm"
      v-if="ready"
      :total-number-of-steps="countSteps"
      :current-step-index="currentStepIndex"
      :title="title"
      :has-back-button="uiHasBackButton"
      :is-first-step="isFirstStep"
    >
      <StepRender
        :step="currentStep"
      />
    </Component>

    <component
      :is="uiModalAndEndPage"
      :end-page-data="endPageData"
    />

    <div class="form-virtual-dom">
      <slot />

      <slot
        v-if="addedModels"
        name="conditions"
        :fields-by-name="fieldsByName"
        :fields-in-array="fieldsInArray"
      />
    </div>
  </div>
</template>

<script>
import Form from '@/lib/models/form';
import flowManager from '@/lib/services/flow-manager';
import router from '@/router';

import StepRender from '@/lib/components/renders/step';
import toCamelCase from '@/lib/helpers/to-camel-case';
import nameValidation from '@/lib/helpers/name-validator';

const extractFieldToReturn = field => ({
  field,
  name: field.name,
});

const createModel = (component, options) => {
  const model = new Form(component, options);

  return model;
};

export default {
  name: 'MainForm',

  components: {
    StepRender,
  },

  props: {
    name: {
      type: String,
      required: true,
    },

    uiForm: {
      type: Object,
      required: true,
    },

    showStep: {
      type: Boolean,
      required: true,
    },

    logicCallbacks: {
      type: Object,
      required: true,
    },
    uiTitle: {
      type: String,
      default: null,
    },
    uiHasBackButton: {
      type: Boolean,
      default: true,
    },

    uiModalAndEndPage: {
      type: Object,
      required: true,
    },

    endPageData: {
      type: Object,
      required: true,
    },
  },

  data: () => ({
    addedConditions: false,
    addedModels: false,
  }),

  computed: {
    fieldsInArray() {
      if (this.model) {
        const { fields } = this.model;

        return fields;
      }

      return [];
    },

    fieldsByName() {
      if (this.model) {
        const { fields } = this.model;

        const toReturn = fields.reduce((red, field) => {
          const { name } = field;

          red[toCamelCase(name)] = extractFieldToReturn(field);

          return red;
        }, {});
        return toReturn;
      }

      return {};
    },

    countSteps() {
      return this.model.steps.length;
    },

    ready() {
      return flowManager.isAlreadyInitialized && this.showStep;
    },

    currentStep() {
      return flowManager.currentStep;
    },

    currentStepIndex() {
      return this.model.steps.indexOf(this.currentStep) + 1;
    },

    isFirstStep() {
      return flowManager.isFirstStep;
    },

    title() {
      return this.currentStep.uiProps.title || this.uiTitle;
    },
  },

  mounted() {
    const { logicCallbacks } = this;
    flowManager.setupCurrentForm(
      this.model,
      logicCallbacks,
    );

    if (nameValidation()) {
      router.loadedForm();
    }
  },

  provide() {
    if (!this.model) {
      this.model = createModel(this, { name: this.name });
    }

    return {
      getForm: () => this.model,
    };
  },
};
</script>

<style>
  .form-virtual-dom {
    display: none;
  }
</style>
